function _typeof(obj) { if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof = function _typeof(obj) { return typeof obj; }; } else { _typeof = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof(obj); }

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); return Constructor; }

/**
 * @license Copyright (c) 2003-2021, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

/**
 * @module typing/textwatcher
 */
import mix from '@ckeditor/ckeditor5-utils/src/mix';
import ObservableMixin from '@ckeditor/ckeditor5-utils/src/observablemixin';
import getLastTextLine from './utils/getlasttextline';
/**
 * The text watcher feature.
 *
 * Fires the {@link module:typing/textwatcher~TextWatcher#event:matched:data `matched:data`},
 * {@link module:typing/textwatcher~TextWatcher#event:matched:selection `matched:selection`} and
 * {@link module:typing/textwatcher~TextWatcher#event:unmatched `unmatched`} events on typing or selection changes.
 *
 * @private
 * @mixes module:utils/observablemixin~ObservableMixin
 */

var TextWatcher =
/*#__PURE__*/
function () {
  /**
   * Creates a text watcher instance.
   *
   * @param {module:engine/model/model~Model} model
   * @param {Function} testCallback See {@link module:typing/textwatcher~TextWatcher#testCallback}.
   */
  function TextWatcher(model, testCallback) {
    var _this = this;

    _classCallCheck(this, TextWatcher);

    /**
     * The editor's model.
     *
     * @readonly
     * @member {module:engine/model/model~Model}
     */
    this.model = model;
    /**
     * The function used to match the text.
     *
     * The test callback can return 3 values:
     *
     * * `false` if there is no match,
     * * `true` if there is a match,
     * * an object if there is a match and we want to pass some additional information to the {@link #event:matched:data} event.
     *
     * @member {Function} #testCallback
     * @returns {Object} testResult
     */

    this.testCallback = testCallback;
    /**
     * Whether there is a match currently.
     *
     * @readonly
     * @member {Boolean}
     */

    this.hasMatch = false;
    /**
     * Flag indicating whether the `TextWatcher` instance is enabled or disabled.
     * A disabled TextWatcher will not evaluate text.
     *
     * To disable TextWatcher:
     *
     *		const watcher = new TextWatcher( editor.model, testCallback );
     *
     *		// After this a testCallback will not be called.
     *		watcher.isEnabled = false;
     *
     * @observable
     * @member {Boolean} #isEnabled
     */

    this.set('isEnabled', true); // Toggle text watching on isEnabled state change.

    this.on('change:isEnabled', function () {
      if (_this.isEnabled) {
        _this._startListening();
      } else {
        _this.stopListening(model.document.selection);

        _this.stopListening(model.document);
      }
    });

    this._startListening();
  }
  /**
   * Starts listening to the editor for typing and selection events.
   *
   * @private
   */


  _createClass(TextWatcher, [{
    key: "_startListening",
    value: function _startListening() {
      var _this2 = this;

      var model = this.model;
      var document = model.document;
      this.listenTo(document.selection, 'change:range', function (evt, _ref) {
        var directChange = _ref.directChange;

        // Indirect changes (i.e. when the user types or external changes are applied) are handled in the document's change event.
        if (!directChange) {
          return;
        } // Act only on collapsed selection.


        if (!document.selection.isCollapsed) {
          if (_this2.hasMatch) {
            _this2.fire('unmatched');

            _this2.hasMatch = false;
          }

          return;
        }

        _this2._evaluateTextBeforeSelection('selection');
      });
      this.listenTo(document, 'change:data', function (evt, batch) {
        if (batch.type == 'transparent') {
          return;
        }

        _this2._evaluateTextBeforeSelection('data', {
          batch: batch
        });
      });
    }
    /**
     * Checks the editor content for matched text.
     *
     * @fires matched:data
     * @fires matched:selection
     * @fires unmatched
     *
     * @private
     * @param {'data'|'selection'} suffix A suffix used for generating the event name.
     * @param {Object} data Data object for event.
     */

  }, {
    key: "_evaluateTextBeforeSelection",
    value: function _evaluateTextBeforeSelection(suffix) {
      var data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      var model = this.model;
      var document = model.document;
      var selection = document.selection;
      var rangeBeforeSelection = model.createRange(model.createPositionAt(selection.focus.parent, 0), selection.focus);

      var _getLastTextLine = getLastTextLine(rangeBeforeSelection, model),
          text = _getLastTextLine.text,
          range = _getLastTextLine.range;

      var testResult = this.testCallback(text);

      if (!testResult && this.hasMatch) {
        this.fire('unmatched');
      }

      this.hasMatch = !!testResult;

      if (testResult) {
        var eventData = Object.assign(data, {
          text: text,
          range: range
        }); // If the test callback returns an object with additional data, assign the data as well.

        if (_typeof(testResult) == 'object') {
          Object.assign(eventData, testResult);
        }

        this.fire("matched:".concat(suffix), eventData);
      }
    }
  }]);

  return TextWatcher;
}();

export { TextWatcher as default };
mix(TextWatcher, ObservableMixin);
/**
 * Fired whenever the text watcher found a match for data changes.
 *
 * @event matched:data
 * @param {Object} data Event data.
 * @param {String} data.text The full text before selection to which the regexp was applied.
 * @param {module:engine/model/range~Range} data.range The range representing the position of the `data.text`.
 * @param {Object} [data.testResult] The additional data returned from the {@link module:typing/textwatcher~TextWatcher#testCallback}.
 */

/**
 * Fired whenever the text watcher found a match for selection changes.
 *
 * @event matched:selection
 * @param {Object} data Event data.
 * @param {String} data.text The full text before selection.
 * @param {module:engine/model/range~Range} data.range The range representing the position of the `data.text`.
 * @param {Object} [data.testResult] The additional data returned from the {@link module:typing/textwatcher~TextWatcher#testCallback}.
 */

/**
 * Fired whenever the text does not match anymore. Fired only when the text watcher found a match.
 *
 * @event unmatched
 */